import {
  Box,
  Link,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
} from "@mui/material";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSlug from "rehype-slug";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { Code } from "~/components/code";
import { isExternalLink } from "~/lib/isExternalLink";

export type MarkdownProps = {
  source: string;
  trusted?: boolean;
  copyButton?: boolean;
  externalLinksInNew?: boolean;
  dataId?: string;
  className?: string;
  sx?: SxProps<Theme>;
};

export function Markdown({
  source,
  trusted = false,
  copyButton = true,
  externalLinksInNew = false,
  dataId,
  className,
  sx,
}: MarkdownProps) {
  const output = useMemo(() => {
    return (
      <ReactMarkdown
        rehypePlugins={[
          // Adds `id` anchors to headings
          rehypeSlug,
          // Allow raw html from trusted source
          trusted ? rehypeRaw : undefined,
        ].flatMap((p) => p ?? [])}
        remarkPlugins={[
          // Support Github-flavored markdown syntax
          remarkGfm,
          // Render newlines as `<br>` elements
          [remarkBreaks],
        ]}
        components={{
          // Render code blocks with Code component
          pre({ children }) {
            return <Code copyButton={copyButton}>{children}</Code>;
          },
          // Render links with MUI Link component
          a({ children, href }) {
            const target =
              externalLinksInNew && isExternalLink(href) ? "_blank" : "_self";
            return (
              <Link
                href={href}
                sx={{ textDecoration: "underline" }}
                target={target}
              >
                {children}
              </Link>
            );
          },
          // Render tables with MUI Table components
          table({ children }) {
            return (
              <TableContainer>
                <Table>{children}</Table>
              </TableContainer>
            );
          },
          thead({ children }) {
            return <TableHead>{children}</TableHead>;
          },
          tbody({ children }) {
            return <TableBody>{children}</TableBody>;
          },
          tr({ children }) {
            return <TableRow>{children}</TableRow>;
          },
          th({ children, style }) {
            return (
              <TableCell
                component="th"
                sx={{
                  // Support cell alignment within delimiter rows
                  // https://github.github.com/gfm/#delimiter-row
                  textAlign: style?.textAlign,
                }}
              >
                {children}
              </TableCell>
            );
          },
          td({ children, style }) {
            return (
              <TableCell
                component="td"
                sx={{
                  // Support cell alignment within delimiter rows
                  // https://github.github.com/gfm/#delimiter-row
                  textAlign: style?.textAlign,
                }}
              >
                {children}
              </TableCell>
            );
          },
        }}
      >
        {source}
      </ReactMarkdown>
    );
  }, [source]);

  return (
    <Box
      className={className}
      data-id={dataId}
      data-testid="markdown"
      sx={[{ minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {output}
    </Box>
  );
}
